import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Plus, Trash2, TrendingUp, Wallet } from "lucide-react";
import { formatINR, parseINR } from "@/lib/format";

const MONTHS = [
  ["01", "Jan"], ["02", "Feb"], ["03", "Mar"], ["04", "Apr"],
  ["05", "May"], ["06", "Jun"], ["07", "Jul"], ["08", "Aug"],
  ["09", "Sep"], ["10", "Oct"], ["11", "Nov"], ["12", "Dec"],
];

const empty = {
  rent_timing: "end_of_month",
  rent_amount: "",
  escalations: [],
  parkings_4w: 0,
  parkings_2w: 0,
  maintenance_by: "tenant",
  deposit_amount: "",
  deposit_months: 0,
  ground_floor_area: "",
  mezzanine_floor_area: "",
  tenant_name: "",
  agreement_start: "",
  agreement_end: "",
  lock_in_tenant_until: "",
  lock_in_landlord_until: "",
  notes: "",
};

const GST_RATE = 0.18;

export default function InfoTab({ propertyId, initialTenantName }) {
  const { user } = useAuth();
  const isAdmin = user && user !== false && user.role === "admin";
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/info`);
      // Migration: if old carpet_area exists but new fields don't, treat it as ground floor
      const hasNewFields = data.ground_floor_area || data.mezzanine_floor_area;
      const groundFromLegacy = !hasNewFields && data.carpet_area ? data.carpet_area : 0;
      // Migration: if old `parkings` exists but new split doesn't, treat as 4-wheeler
      const hasNewParkings = data.parkings_4w || data.parkings_2w;
      const fourFromLegacy = !hasNewParkings && data.parkings ? data.parkings : 0;
      setForm({
        rent_timing: data.rent_timing || "end_of_month",
        rent_amount: data.rent_amount ? String(data.rent_amount) : "",
        escalations: Array.isArray(data.escalations)
          ? data.escalations.map((es) => {
              const date = es.date || (es.year && es.month
                ? `${String(es.year).padStart(4, "0")}-${String(es.month).padStart(2, "0")}-${String(es.day || "01").padStart(2, "0")}`
                : "");
              const [y, m, d] = (date || "").split("-");
              return {
                date,
                date_to: es.date_to || "",
                day: d || es.day || "",
                month: m || es.month || "",
                year: y || es.year || "",
                new_amount: es.new_amount,
              };
            })
          : [],
        parkings_4w: data.parkings_4w ?? fourFromLegacy,
        parkings_2w: data.parkings_2w ?? 0,
        maintenance_by: data.maintenance_by || "tenant",
        deposit_amount: data.deposit_amount ? String(data.deposit_amount) : "",
        deposit_months: data.deposit_months ?? 0,
        ground_floor_area: data.ground_floor_area
          ? String(data.ground_floor_area)
          : (groundFromLegacy ? String(groundFromLegacy) : ""),
        mezzanine_floor_area: data.mezzanine_floor_area ? String(data.mezzanine_floor_area) : "",
        tenant_name: data.tenant_name || initialTenantName || "",
        agreement_start: data.agreement_start || "",
        agreement_end: data.agreement_end || "",
        lock_in_tenant_until: data.lock_in_tenant_until || "",
        lock_in_landlord_until: data.lock_in_landlord_until || "",
        notes: data.notes || "",
      });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyId]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        rent_timing: form.rent_timing,
        rent_amount: parseFloat(form.rent_amount) || 0,
        escalations: form.escalations
          .filter((es) => es.date && es.new_amount)
          .map((es) => {
            const [y, m, d] = (es.date || "").split("-");
            return {
              date: es.date,
              date_to: es.date_to || "",
              day: d || "",
              month: m || "",
              year: y || "",
              new_amount: parseFloat(es.new_amount) || 0,
            };
          }),
        parkings_4w: parseInt(form.parkings_4w, 10) || 0,
        parkings_2w: parseInt(form.parkings_2w, 10) || 0,
        parkings: (parseInt(form.parkings_4w, 10) || 0) + (parseInt(form.parkings_2w, 10) || 0),
        maintenance_by: form.maintenance_by,
        deposit_amount: parseFloat(form.deposit_amount) || 0,
        deposit_months: parseInt(form.deposit_months, 10) || 0,
        ground_floor_area: parseFloat(form.ground_floor_area) || 0,
        mezzanine_floor_area: parseFloat(form.mezzanine_floor_area) || 0,
        carpet_area: (parseFloat(form.ground_floor_area) || 0) + (parseFloat(form.mezzanine_floor_area) || 0),
        tenant_name: form.tenant_name || "",
        agreement_start: form.agreement_start || "",
        agreement_end: form.agreement_end || "",
        lock_in_tenant_until: form.lock_in_tenant_until || "",
        lock_in_landlord_until: form.lock_in_landlord_until || "",
        notes: form.notes,
      };
      await api.put(`/properties/${propertyId}/info`, payload);
      toast.success("Information saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addEscalation = () => {
    const nextYear = new Date().getFullYear() + 1;
    const dateStr = `${nextYear}-01-01`;
    setForm({
      ...form,
      escalations: [
        ...form.escalations,
        { date: dateStr, date_to: "", day: "01", month: "01", year: String(nextYear), new_amount: "" },
      ],
    });
  };

  const updateEscalation = (i, key, value) => {
    const next = [...form.escalations];
    next[i] = { ...next[i], [key]: value };
    setForm({ ...form, escalations: next });
  };

  const removeEscalation = (i) => {
    setForm({ ...form, escalations: form.escalations.filter((_, idx) => idx !== i) });
  };

  if (loading) {
    return <div className="h-64 rounded-xl bg-white border border-stone-200 animate-pulse" />;
  }

  return (
    <div className="max-w-3xl [&_input]:text-base [&_input]:font-medium [&_textarea]:text-base [&_textarea]:font-medium [&_[role=combobox]]:text-base [&_[role=combobox]]:font-medium">
      <div className="mb-6">
        <h2 className="text-2xl font-medium text-stone-900">Information</h2>
        <p className="text-base text-stone-600 mt-1">Lease terms, rent schedule, parkings and maintenance.</p>
      </div>

      <Card className="border-stone-200 bg-white rounded-xl p-6 sm:p-8">
        <form onSubmit={submit} className="space-y-6" data-testid="info-form">
          <fieldset disabled={!isAdmin} className="space-y-6 disabled:opacity-95">
            {/* Tenant */}
            <div className="space-y-3">
              <div>
                <Label className="text-base font-bold text-stone-900">Tenant</Label>
                <p className="text-sm font-medium text-stone-600 mt-1">Tenant name and agreement period.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="t-name" className="text-sm font-bold text-stone-700">Tenant name</Label>
                  <Input
                    id="t-name"
                    value={form.tenant_name}
                    onChange={(e) => setForm({ ...form, tenant_name: e.target.value })}
                    placeholder="Full name"
                    data-testid="info-tenant-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t-start" className="text-sm font-bold text-stone-700">Agreement start</Label>
                  <Input
                    id="t-start"
                    type="date"
                    value={form.agreement_start}
                    onChange={(e) => setForm({ ...form, agreement_start: e.target.value })}
                    data-testid="info-agreement-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="t-end" className="text-sm font-bold text-stone-700">Agreement end</Label>
                  <Input
                    id="t-end"
                    type="date"
                    value={form.agreement_end}
                    onChange={(e) => setForm({ ...form, agreement_end: e.target.value })}
                    data-testid="info-agreement-end"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label>Rent timing</Label>
                <Select value={form.rent_timing} onValueChange={(v) => setForm({ ...form, rent_timing: v })} disabled={!isAdmin}>
                  <SelectTrigger data-testid="info-timing-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="advance">Rent in advance</SelectItem>
                    <SelectItem value="end_of_month">End of the month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Maintenance paid by</Label>
                <Select value={form.maintenance_by} onValueChange={(v) => setForm({ ...form, maintenance_by: v })} disabled={!isAdmin}>
                  <SelectTrigger data-testid="info-maintenance-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="landlord">Landlord</SelectItem>
                    <SelectItem value="tenant">Tenant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="rent">Rent amount (₹)</Label>
                <Input
                  id="rent"
                  type="text"
                  inputMode="numeric"
                  value={formatINR(form.rent_amount)}
                  onChange={(e) => setForm({ ...form, rent_amount: parseINR(e.target.value) })}
                  placeholder="e.g. 25,000"
                  data-testid="info-rent-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="gst">GST @ 18% (₹)</Label>
                <Input
                  id="gst"
                  type="text"
                  readOnly
                  value={formatINR(Math.round((parseFloat(form.rent_amount) || 0) * GST_RATE))}
                  placeholder="auto"
                  className="bg-stone-50 text-stone-700"
                  data-testid="info-gst-display"
                />
                {(parseFloat(form.rent_amount) || 0) > 0 && (
                  <p className="text-sm font-bold text-stone-700">
                    Total payable: ₹{formatINR(Math.round((parseFloat(form.rent_amount) || 0) * (1 + GST_RATE)))}
                  </p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label className="text-base font-bold text-stone-900">Parkings</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="park4" className="text-sm font-bold text-stone-700">4-wheeler</Label>
                    <Input
                      id="park4"
                      type="number"
                      min="0"
                      step="1"
                      value={form.parkings_4w}
                      onChange={(e) => setForm({ ...form, parkings_4w: e.target.value })}
                      data-testid="info-parkings-4w-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="park2" className="text-sm font-bold text-stone-700">2-wheeler</Label>
                    <Input
                      id="park2"
                      type="number"
                      min="0"
                      step="1"
                      value={form.parkings_2w}
                      onChange={(e) => setForm({ ...form, parkings_2w: e.target.value })}
                      data-testid="info-parkings-2w-input"
                    />
                  </div>
                </div>
                <div className="rounded-md bg-stone-50 border border-stone-200 px-4 py-2 inline-flex items-center gap-2 text-sm">
                  <span className="text-stone-500">Total parkings:</span>
                  <span className="font-medium text-stone-900" data-testid="info-total-parkings">
                    {(parseInt(form.parkings_4w, 10) || 0) + (parseInt(form.parkings_2w, 10) || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Carpet Area */}
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-base font-bold text-stone-900">Carpet area</Label>
                <p className="text-sm font-medium text-stone-600 mt-1">Ground + Mezzanine = total carpet area.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="ground" className="text-sm font-bold text-stone-700">Ground floor area (sq ft)</Label>
                  <Input
                    id="ground"
                    type="text"
                    inputMode="numeric"
                    value={formatINR(form.ground_floor_area)}
                    onChange={(e) => setForm({ ...form, ground_floor_area: parseINR(e.target.value) })}
                    placeholder="e.g. 800"
                    data-testid="info-ground-area-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="mezz" className="text-sm font-bold text-stone-700">Mezzanine floor area (sq ft)</Label>
                  <Input
                    id="mezz"
                    type="text"
                    inputMode="numeric"
                    value={formatINR(form.mezzanine_floor_area)}
                    onChange={(e) => setForm({ ...form, mezzanine_floor_area: parseINR(e.target.value) })}
                    placeholder="e.g. 150"
                    data-testid="info-mezz-area-input"
                  />
                </div>
              </div>
              {(parseFloat(form.ground_floor_area) || 0) + (parseFloat(form.mezzanine_floor_area) || 0) >= 0 && (
                <div className="rounded-md bg-stone-50 border border-stone-200 px-4 py-2 inline-flex items-center gap-2 text-sm">
                  <span className="text-stone-500">Total carpet area:</span>
                  <span className="font-medium text-stone-900" data-testid="info-total-area">
                    {formatINR((parseFloat(form.ground_floor_area) || 0) + (parseFloat(form.mezzanine_floor_area) || 0))} sq ft
                  </span>
                </div>
              )}
            </div>

            {/* Deposit */}
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-stone-900 flex items-center gap-1.5">
                  <Wallet className="h-4 w-4 text-emerald-900" strokeWidth={1.5} />
                  Deposit
                </Label>
                <p className="text-sm font-medium text-stone-600 mt-1">Security deposit collected at the start of the lease.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="dep-amt">Deposit amount (₹)</Label>
                  <Input
                    id="dep-amt"
                    type="text"
                    inputMode="numeric"
                    value={formatINR(form.deposit_amount)}
                    onChange={(e) => setForm({ ...form, deposit_amount: parseINR(e.target.value) })}
                    placeholder="e.g. 1,00,000"
                    data-testid="info-deposit-amount"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dep-mon">Equivalent months of rent</Label>
                  <Input
                    id="dep-mon"
                    type="number"
                    min="0"
                    step="1"
                    value={form.deposit_months}
                    onChange={(e) => setForm({ ...form, deposit_months: e.target.value })}
                    placeholder="e.g. 4"
                    data-testid="info-deposit-months"
                  />
                </div>
              </div>
            </div>

            {/* Lock-in period */}
            <div className="space-y-3 pt-2">
              <div>
                <Label className="text-base font-bold text-stone-900">Lock-in period</Label>
                <p className="text-sm font-medium text-stone-600 mt-1">Date until each side cannot exit the lease.</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2">
                  <Label htmlFor="lock-tenant" className="text-sm font-bold text-stone-700">Tenant lock-in until</Label>
                  <Input
                    id="lock-tenant"
                    type="date"
                    value={form.lock_in_tenant_until === "NIL" ? "" : form.lock_in_tenant_until}
                    onChange={(e) => setForm({ ...form, lock_in_tenant_until: e.target.value })}
                    disabled={form.lock_in_tenant_until === "NIL" || !isAdmin}
                    placeholder={form.lock_in_tenant_until === "NIL" ? "NIL" : ""}
                    data-testid="info-lockin-tenant"
                  />
                  <label className="flex items-center gap-2 text-sm font-medium text-stone-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-stone-300 text-emerald-900 focus:ring-emerald-900"
                      checked={form.lock_in_tenant_until === "NIL"}
                      onChange={(e) => setForm({ ...form, lock_in_tenant_until: e.target.checked ? "NIL" : "" })}
                      disabled={!isAdmin}
                      data-testid="info-lockin-tenant-nil"
                    />
                    NIL — no lock-in for tenant
                  </label>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lock-landlord" className="text-sm font-bold text-stone-700">Landlord lock-in until</Label>
                  <Input
                    id="lock-landlord"
                    type="date"
                    value={form.lock_in_landlord_until === "NIL" ? "" : form.lock_in_landlord_until}
                    onChange={(e) => setForm({ ...form, lock_in_landlord_until: e.target.value })}
                    disabled={form.lock_in_landlord_until === "NIL" || !isAdmin}
                    placeholder={form.lock_in_landlord_until === "NIL" ? "NIL" : ""}
                    data-testid="info-lockin-landlord"
                  />
                  <label className="flex items-center gap-2 text-sm font-medium text-stone-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 rounded border-stone-300 text-emerald-900 focus:ring-emerald-900"
                      checked={form.lock_in_landlord_until === "NIL"}
                      onChange={(e) => setForm({ ...form, lock_in_landlord_until: e.target.checked ? "NIL" : "" })}
                      disabled={!isAdmin}
                      data-testid="info-lockin-landlord-nil"
                    />
                    NIL — no lock-in for landlord
                  </label>
                </div>
              </div>
            </div>


            {/* Escalations */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-stone-900 flex items-center gap-1.5">
                    <TrendingUp className="h-4 w-4 text-emerald-900" strokeWidth={1.5} />
                    Rent escalations
                  </Label>
                  <p className="text-sm font-medium text-stone-600 mt-1">When does the rent step up, and to what amount?</p>
                </div>
                {isAdmin && (
                  <Button type="button" size="sm" variant="outline" onClick={addEscalation} data-testid="add-escalation">
                    <Plus className="h-3.5 w-3.5 mr-1" strokeWidth={1.5} />
                    Add
                  </Button>
                )}
              </div>

              {form.escalations.length === 0 ? (
                <p className="text-sm text-stone-400 italic border border-dashed border-stone-200 rounded-md p-4 text-center">
                  No escalations scheduled.
                </p>
              ) : (
                <ul className="space-y-3">
                  {form.escalations.map((es, i) => {
                    const escAmt = parseFloat(es.new_amount) || 0;
                    const escGst = Math.round(escAmt * GST_RATE);
                    return (
                      <li key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 items-end" data-testid={`escalation-${i}`}>
                        <div className="col-span-2 sm:col-span-3 space-y-1.5">
                          <Label className="text-sm font-bold text-stone-700">Effective from</Label>
                          <Input
                            type="date"
                            value={es.date || ""}
                            onChange={(e) => {
                              const date = e.target.value;
                              const [y, m, d] = (date || "").split("-");
                              const next = [...form.escalations];
                              next[i] = { ...next[i], date, day: d || "", month: m || "", year: y || "" };
                              setForm({ ...form, escalations: next });
                            }}
                            data-testid={`escalation-date-${i}`}
                          />
                        </div>
                        <div className="col-span-2 sm:col-span-3 space-y-1.5">
                          <Label className="text-sm font-bold text-stone-700">Effective to (optional)</Label>
                          <Input
                            type="date"
                            value={es.date_to || ""}
                            onChange={(e) => updateEscalation(i, "date_to", e.target.value)}
                            data-testid={`escalation-date-to-${i}`}
                          />
                        </div>
                        <div className="col-span-1 sm:col-span-2 space-y-1.5">
                          <Label className="text-sm font-bold text-stone-700">New rent (₹)</Label>
                          <Input
                            type="text"
                            inputMode="numeric"
                            value={formatINR(es.new_amount)}
                            onChange={(e) => updateEscalation(i, "new_amount", parseINR(e.target.value))}
                            placeholder="28,000"
                          />
                        </div>
                        <div className="col-span-1 sm:col-span-3 space-y-1.5">
                          <Label className="text-sm font-bold text-stone-700">GST @ 18% (₹)</Label>
                          <Input
                            type="text"
                            readOnly
                            value={formatINR(escGst)}
                            placeholder="auto"
                            className="bg-stone-50 text-stone-700"
                            data-testid={`escalation-gst-${i}`}
                          />
                        </div>
                        <div className="col-span-2 sm:col-span-1 flex sm:justify-end">
                          {isAdmin && (
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeEscalation(i)} className="text-stone-500 hover:text-red-700" data-testid={`remove-escalation-${i}`}>
                              <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                          )}
                        </div>
                        {escAmt > 0 && es.date && (
                          <p className="col-span-2 sm:col-span-12 text-xs text-stone-500 -mt-1">
                            Total payable from {new Date(es.date).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
                            {es.date_to ? ` until ${new Date(es.date_to).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}` : ""}: ₹{formatINR(Math.round(escAmt * (1 + GST_RATE)))}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea id="notes" rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Any additional terms or remarks…" data-testid="info-notes" />
            </div>
          </fieldset>

          {isAdmin && (
            <div className="flex justify-end pt-2">
              <Button type="submit" disabled={saving} className="bg-emerald-900 hover:bg-emerald-800 text-stone-50" data-testid="info-save">
                <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {saving ? "Saving…" : "Save information"}
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}
